import { JobCriteriaData } from "components/job-criteria/types";
import { normalizeWorkMode } from "utils/workMode";

export const mapJsonToJobCriteriaData = (jsonData: any): JobCriteriaData => {
  // Helper to filter out null/undefined entries and map to array
  const filterNull = <T>(items: (T | null | undefined)[]): T[] => items.filter((item): item is T => !!item);

  const workCulture = jsonData?.work_culture || {};

  const workMotivators = (() => {
    const result: { title: string; description: string; checked: boolean }[] = [];
    let idx = 1;
    while (workCulture[`culture${idx}`]) {
      result.push({
        title: workCulture[`culture${idx}`],
        description: workCulture[`culture_expectation${idx}`] ?? "",
        checked: workCulture[`check${idx}`] ?? false,
      });
      idx++;
    }
    return result;
  })();

  const qualityDescriptions: Record<string, string> = workCulture?.quality_options_descriptions || {};

  const qualityOptions = (workCulture?.quality_options || []).map((option: any) =>
    typeof option === 'string'
      ? { title: option, description: qualityDescriptions[option] || '' }
      : { title: option?.title ?? '', description: option?.description ?? qualityDescriptions[option?.title] ?? '' }
  );

  const workplaceQualities = filterNull([
    workCulture?.quality1,
    workCulture?.quality2,
    workCulture?.quality3,
  ]).map((quality) => ({
    title: quality,
    description: qualityDescriptions[quality] || '',
    checked: true,
  }));

  return {
    // The talent audit criteria payload carries no basic expectation. Rebuilding
    // an object of empty strings here would render a blank section, so hand back
    // null and let EnhancedJobCriteria skip it.
    basicExpectation: !jsonData?.basic_expectation ? null : {
      employmentType: jsonData?.basic_expectation?.employment_type ?? "",
      workMode: normalizeWorkMode(jsonData?.basic_expectation?.work_mode),
      jobLocation: jsonData?.basic_expectation?.address ?? "",
      experienceLevel: jsonData?.basic_expectation?.experience_level ?? "",
      yearsOfExperience: jsonData?.basic_expectation?.years_of_experience?.toString() ?? "0",
      workEnvironment: jsonData?.basic_expectation?.experience_environment ?? '',
      minSalary: jsonData?.basic_expectation?.min_salary_range_amount?.toString() ?? "",
      maxSalary: jsonData?.basic_expectation?.max_salary_range_amount?.toString() ?? "",
      salaryRange: jsonData?.basic_expectation?.salary_range_currency
        ? `${jsonData.basic_expectation.salary_range_currency} ${jsonData.basic_expectation.min_salary_range_amount ?? 0} - ${jsonData.basic_expectation.max_salary_range_amount ?? 0} ${jsonData.basic_expectation.salary_range_term ?? ""}`
        : "",
      salaryCurrency: jsonData?.basic_expectation?.salary_range_currency ?? "",
      salaryPeriod: jsonData?.basic_expectation?.salary_range_term ?? "",
      isEmploymentType: jsonData?.basic_expectation?.require_employment_type ?? false,
      isWorkMode: jsonData?.basic_expectation?.require_work_mode ?? true,
      isExperienceLevel: jsonData?.basic_expectation?.require_experience_level ?? false,
      isYearsOfExperience: jsonData?.basic_expectation?.require_years_of_experience ?? false,
      isWorkEnvironment: jsonData?.basic_expectation?.require_work_environment ?? false,
      isSalaryRange: jsonData?.basic_expectation?.require_salary_range ?? false,
      displaySalary: jsonData?.basic_expectation?.display_salary ?? false,
      isCV: jsonData?.basic_expectation?.require_cv_upload ?? false,
      isPortfolio: jsonData?.basic_expectation?.require_portfolio_upload ?? true,
    },
    coreResponsibilities: {
      responsibilities: (() => {
        const cr = jsonData?.core_responsibilities || {};
        const result: { title: string; description: string; checked: boolean }[] = [];
        let idx = 1;
        while (cr[`responsibility${idx}`]) {
          result.push({
            title: cr[`responsibility${idx}`],
            description: cr[`description${idx}`] ?? "",
            checked: cr[`check${idx}`] ?? true,
          });
          idx++;
        }
        return result;
      })(),
    },
    technicalSkillsHeading: jsonData?.uses_operational_competence
      ? 'Operational competence'
      : 'Technical Skills',
    technicalSkills: {
      technicalSkills: (() => {
        const ts = jsonData?.technical_skills || {};
        const result: { title: string; expectation: string }[] = [];
        let idx = 1;
        while (ts[`skill${idx}`]) {
          result.push({
            title: ts[`skill${idx}`],
            expectation: ts[`expectation${idx}`] ?? "",
          });
          idx++;
        }
        return result;
      })(),
    },
    softSkills: {
      softSkills: (() => {
        const ss = jsonData?.soft_skills || {};
        const result: { title: string; description: string; checked: boolean; rating: number }[] = [];
        let idx = 1;
        while (ss[`skill${idx}`]) {
          result.push({
            title: ss[`skill${idx}`],
            description: ss[`skill_expectation${idx}`] ?? "",
            checked: ss[`check${idx}`] ?? false,
            rating: ss[`skill${idx}_rating`] ?? 0,
          });
          idx++;
        }
        return result;
      })(),
    },
    workMotivators: {
      workMotivators,
    },
    workplaceQualities: {
      workplaceQualities,
      qualityOptions,
    },
    workStyles: {
      workStyles: (() => {
        const result: { description: string; rating: number }[] = [];
        let idx = 1;
        while (workCulture[`work_style${idx}`]) {
          result.push({
            description: workCulture[`work_style${idx}`],
            rating: workCulture[`work_style${idx}_rating`] ?? 0,
          });
          idx++;
        }
        return result;
      })(),
    },
    personalityAlignments: {
      personalityAlignments: (() => {
        const p = jsonData?.personality || {};
        const result: {
          category: string; description: string; style: string; implication: string;
          mostPreferred: string; nextPreferred: string; leastPreferred: string;
          mostPreferredDescription: string;
        }[] = [];
        let idx = 1;
        while (p[`quality${idx}`]) {
          result.push({
            category: p[`quality${idx}`],
            description: p[`description${idx}`] ?? "",
            style: p[`most_prefered${idx}`] ?? "",
            implication: p[`next_prefered${idx}`] ?? "",
            mostPreferred: p[`most_prefered${idx}`] ?? "",
            nextPreferred: p[`next_prefered${idx}`] ?? "",
            leastPreferred: p[`least_prefered${idx}`] ?? "",
            mostPreferredDescription: p[`most_prefered_description${idx}`] ?? "",
          });
          idx++;
        }
        return result;
      })(),
    },
    workCultureTraits: {
      workCultureTraits: workMotivators,
      candidateQualities: workplaceQualities.map(({ title }) => title),
      qualityOptions,
    },
  };
};
